// Latin-extended → ASCII transliteration map. The send pipeline applies
// this to outbound SMS bodies because most carriers' GSM-7 fallback to
// UCS-2 doubles the per-segment cost; transliterating ahead of time
// keeps messages on GSM-7 and at the lower cost. The same map is mirrored
// on the server (`server/src/Utils/transliterate-for-sms.ts`) so the
// displayed character/segment budget matches what providers see at send
// time (DEV-8042). Keep both copies in sync — `shared/` cannot be used
// because it is not picked up by both build setups.
//
// Coverage:
//   - Slovenian / Croatian / Bosnian-latin / Serbian-latin: čšž ćđ
//   - German: ä ö ü ß
//   - Czech / Slovak: áčďéěíňóřšťúůýž
//   - Polish: ąćęłńóśźż
//   - Romanian: ăâîșț
//   - Hungarian: áéíóöőúüű
//   - Turkish: çğıİöşü
//   - Spanish / Portuguese: áàâãéêíóôõúñç
//
// Casing is mirrored: every lowercase replacement has a paired uppercase
// entry (e.g. č→c and Č→C). Multi-character expansions (ß→ss, ä→ae,
// đ→dj) are kept as-is because that is what the provider's transliteration
// would emit.
const TRANSLITERATION_MAP: Record<string, string> = {
  // Slovenian / Croatian / Bosnian-latin / Serbian-latin
  č: "c",
  Č: "C",
  ć: "c",
  Ć: "C",
  đ: "dj",
  Đ: "Dj",
  š: "s",
  Š: "S",
  ž: "z",
  Ž: "Z",
  // German
  ä: "ae",
  Ä: "Ae",
  ö: "oe",
  Ö: "Oe",
  ü: "ue",
  Ü: "Ue",
  ß: "ss",
  // Czech / Slovak (those not already covered)
  á: "a",
  Á: "A",
  ď: "d",
  Ď: "D",
  é: "e",
  É: "E",
  ě: "e",
  Ě: "E",
  í: "i",
  Í: "I",
  ň: "n",
  Ň: "N",
  ó: "o",
  Ó: "O",
  ř: "r",
  Ř: "R",
  ť: "t",
  Ť: "T",
  ú: "u",
  Ú: "U",
  ů: "u",
  Ů: "U",
  ý: "y",
  Ý: "Y",
  // Polish (others not covered)
  ą: "a",
  Ą: "A",
  ę: "e",
  Ę: "E",
  ł: "l",
  Ł: "L",
  ń: "n",
  Ń: "N",
  ś: "s",
  Ś: "S",
  ź: "z",
  Ź: "Z",
  ż: "z",
  Ż: "Z",
  // Romanian
  ă: "a",
  Ă: "A",
  â: "a",
  Â: "A",
  î: "i",
  Î: "I",
  ș: "s",
  Ș: "S",
  ț: "t",
  Ț: "T",
  // Hungarian (others not covered)
  ő: "o",
  Ő: "O",
  ű: "u",
  Ű: "U",
  // Turkish (others not covered)
  ç: "c",
  Ç: "C",
  ğ: "g",
  Ğ: "G",
  ı: "i",
  İ: "I",
  // Spanish / Portuguese (others not covered)
  à: "a",
  À: "A",
  ã: "a",
  Ã: "A",
  ê: "e",
  Ê: "E",
  ô: "o",
  Ô: "O",
  õ: "o",
  Õ: "O",
  ñ: "n",
  Ñ: "N",
  // Non-breaking space: rendered as a space but outside GSM-7, so an
  // unmapped U+00A0 (e.g. inherited from the old `<p>&nbsp;</p>`
  // blank-paragraph format) flips the encoding check to UCS-2 and
  // shrinks the per-segment budget from 160 to 67. Fold to a regular
  // space so the counter, and the send pipeline, both see GSM-7.
  "\u00A0": " ",
};

/**
 * Strips Latin-extended diacritics so the result is plain ASCII for SMS
 * counting and dispatch. Characters not in the map pass through
 * unchanged — emoji, brackets, structural punctuation are preserved.
 */
export function transliterateForSms(text: string): string {
  if (!text) return text;
  let out = "";
  for (const ch of text) {
    out += TRANSLITERATION_MAP[ch] ?? ch;
  }
  return out;
}

// Mirrors the server's SMS_EMOJI_PATTERN (transliterate-for-sms.ts). Emoji
// are absent from GSM-7, so one in an SMS body flips it to UCS-2 and roughly
// doubles the segment cost. \p{Extended_Pictographic} covers both
// supplementary-plane emoji and BMP pictographs salons use (✂ ☀ ❤ ✨ ✅);
// ZWJ and variation selectors are removed too so compound-emoji sequences
// leave no orphaned joiners behind.
const SMS_EMOJI_PATTERN = /\p{Extended_Pictographic}|\u200D|\uFE0E|\uFE0F/gu;

/**
 * Removes emoji and pictographic symbols from a string. Used to keep emoji
 * out of the SMS editor (DEV-8618); the server applies the same strip at
 * save and send time. Whitespace is left untouched so this is safe to run
 * on incremental keystroke/paste input.
 */
export function removeSmsEmoji(text: string): string {
  if (!text) return text;
  return text.replace(SMS_EMOJI_PATTERN, "");
}
